"use strict";
Object.defineProperty(exports, "__esModule", { value: true });
exports.FirebaseEnvironment = void 0;
exports.getFirebaseEnvironment = getFirebaseEnvironment;
exports.withoutEmulatorEnvVars = withoutEmulatorEnvVars;
const tslib_1 = require("tslib");
const clone_1 = tslib_1.__importDefault(require("lodash/clone"));
const omit_1 = tslib_1.__importDefault(require("lodash/omit"));
const zod_1 = require("zod");
/**
 * There are exactly three places we run Firebase at the moment
 *  - production and staging are real projects
 *  - emulated represents when we run Firebase against the emulators
 *
 * We always rewrite prod as production
 */
exports.FirebaseEnvironment = zod_1.z.preprocess((value) => {
    if (value === "prod")
        return "production";
    return value;
}, zod_1.z.enum(["production", "staging", "emulated"]));
/**
 * Safely obtain process.env with DefinePlugin support
 *
 * These variable are actually filled in by the DefinePlugin at bundle time, so they are compile-time values,
 * baked into the build. For this reason, we can't inject the whole of `process.env` as an application service: the
 * define plugin looks specifically for these full property accesses.
 *
 * This makes the accesses of the environment in getConfigFromEnv() work for both bundled apps and Node
 *
 * This function can't be abstracted: the environment variables accesses must appear in the code as the literal
 * "process.env.REACT_APP_ENVIRONMENT_NAME" name for them to be replaced by the bundler
 */
function getProcessEnvSafely() {
    return {
        ...(hasProcessAccess() ? process?.env ?? {} : {}),
        REACT_APP_ENVIRONMENT_NAME: process.env.REACT_APP_ENVIRONMENT_NAME,
        REACT_APP_FIREBASE_ENVIRONMENT: process.env.REACT_APP_FIREBASE_ENVIRONMENT,
    };
}
/**
 * Gets the Firebase environment that should be used, in the current process/context
 *
 * Works in all places safely. In some cases, such as Cypress, you may want to inject the environment variables as a
 * parameter, and those will take precedence over the real environment.
 *
 * The logger is so that we can report invalid environment names as warnings at runtime, it's optional.
 */
function getFirebaseEnvironment(overrideProcessEnv = {}) {
    const env = { ...getProcessEnvSafely(), ...overrideProcessEnv };
    const sources = [
        env.FIREBASE_ENVIRONMENT,
        env.APPLICATION_ENV,
        env.REACT_APP_FIREBASE_ENVIRONMENT,
        env.REACT_APP_ENVIRONMENT_NAME,
    ];
    for (const source of sources) {
        const parsed = exports.FirebaseEnvironment.safeParse(source);
        if (parsed.success) {
            return parsed.data;
        }
    }
    return "production";
}
/**
 * These are env vars used to point at the emulator
 */
const emulatorEnvVars = [
    "FIREBASE_EMULATOR_HUB",
    "FIREBASE_FIRESTORE_EMULATOR_ADDRESS",
    "FIRESTORE_EMULATOR_HOST",
    "FIREBASE_AUTH_EMULATOR_HOST",
];
function hasProcessAccess() {
    try {
        return typeof process !== "undefined";
    }
    catch (err) {
        return false;
    }
}
/**
 * Temporarily clear some environment variables that are used to associate the current connection with the emulator
 *
 * This avoids the emulator env vars taking effect during the inner function
 */
function withoutEmulatorEnvVars(inner) {
    if (hasProcessAccess()) {
        const previousEnvVars = (0, clone_1.default)(process?.env || {});
        try {
            process.env = (0, omit_1.default)(process?.env || {}, ...emulatorEnvVars);
            return inner();
        }
        finally {
            process.env = previousEnvVars;
        }
    }
    else {
        return inner();
    }
}
//# sourceMappingURL=firebaseEnvironment.js.map